// ============================================================================
// AGENT SERVICE
// ============================================================================
// Business logic and API calls for Agent module

import { get, post, put, del } from '../client/axiosClient';
import { agentEndpoints } from '../endpoints/agent.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentSearchRequest,
} from '../types';

/**
 * Agent Service Class
 */
class AgentService {
  /**
   * Get all agents (paginated)
   */
  async getAll(params?: AgentSearchRequest): Promise<PaginatedResponse<Agent>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Agent', params)
      : agentEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get agent by ID
   */
  async getById(id: ID): Promise<ApiResponse<Agent>> {
    const response = await get<ApiResponse<Agent>>(agentEndpoints.getById(id));
    return response.data;
  }

  /**
   * Get agent by code
   */
  async getByCode(code: string): Promise<ApiResponse<Agent>> {
    const response = await get<ApiResponse<Agent>>(agentEndpoints.getByCode(code));
    return response.data;
  }

  /**
   * Create new agent
   */
  async create(data: CreateAgentRequest): Promise<ApiResponse<Agent>> {
    const response = await post<ApiResponse<Agent>>(
      agentEndpoints.create(),
      data
    );
    return response.data;
  }

  /**
   * Update agent
   */
  async update(id: ID, data: UpdateAgentRequest): Promise<ApiResponse<Agent>> {
    const response = await put<ApiResponse<Agent>>(
      agentEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Delete agent
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(agentEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Validate agent code uniqueness
   */
  async validateCode(code: string, excludeId?: ID): Promise<ApiResponse<boolean>> {
    let endpoint = agentEndpoints.validateCode(code);
    if (excludeId) {
      endpoint = `${endpoint}?excludeId=${excludeId}`;
    }
    const response = await get<ApiResponse<boolean>>(endpoint);
    return response.data;
  }
}

/**
 * Export singleton instance
 */
export const agentService = new AgentService();
export default agentService;
